import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { transcribeFileWithStrategy } from '../services/transcriptionRunner';

export type CloudProvider = 'openwhispr' | 'openai' | 'groq' | 'mistral' | 'custom';
export type LocalProvider = 'whisper' | 'nvidia';
export type ProviderMode = 'cloud' | 'local';
export type GroqProcessingMode = 'auto' | 'direct' | 'batch';
export type UploadStatus = 'idle' | 'selected' | 'transcribing' | 'complete' | 'error';

export type SelectedUploadFile = {
  name: string;
  sizeBytes: number;
  browserFile?: File;
};

export type TranscribeOptions = {
  mode: ProviderMode;
  cloudProvider: CloudProvider;
  groqProcessingMode: GroqProcessingMode;
  cloudModel: string;
  localProvider: LocalProvider;
  activeLocalModel: string;
  activeApiKey: string;
  language: string;
  normalizedCustomUrl: string;
  noteTitle?: string;
  noteFolder?: string;
  preflightError?: string;
};

type UploadSessionState = {
  selectedFile: SelectedUploadFile | null;
  status: UploadStatus;
  errorMsg: string;
  progress: number;
  transcribingDetail: string;
  audioDurationSec?: number;
  noteTitle: string;
  noteText: string;
  selectedFolder: string;
  savedNoteId: string | null;
  requestToken: number;
  prepareSelectedFile: (file: SelectedUploadFile) => void;
  setUploadError: (message: string) => void;
  resetUpload: () => void;
  setNoteTitle: (value: string) => void;
  setNoteText: (value: string) => void;
  setSelectedFolder: (value: string) => void;
  setSavedNoteId: (value: string | null) => void;
  transcribe: (options: TranscribeOptions) => Promise<void>;
};

type PersistedUploadSessionState = {
  selectedFile: Omit<SelectedUploadFile, 'browserFile'> | null;
  status: UploadStatus;
  errorMsg: string;
  progress: number;
  transcribingDetail: string;
  audioDurationSec?: number;
  noteTitle: string;
  noteText: string;
  selectedFolder: string;
  savedNoteId: string | null;
};

let progressTimer: ReturnType<typeof setInterval> | null = null;

function stopProgressTimer() {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
}

export const useUploadSessionStore = create<UploadSessionState>()(
  persist(
    (set, get) => {
      const bumpRequestToken = () => {
        const requestToken = get().requestToken + 1;
        set({ requestToken });
        return requestToken;
      };

      const startMockProgress = (requestToken: number) => {
        stopProgressTimer();
        set({ progress: 0 });
        progressTimer = setInterval(() => {
          const { progress, requestToken: activeToken, status } = get();

          if (status !== 'transcribing' || activeToken !== requestToken) {
            stopProgressTimer();
            return;
          }

          if (progress >= 90) {
            return;
          }

          set({ progress: Math.min(90, progress + Math.random() * 7) });
        }, 450);
      };

      return {
        selectedFile: null,
        status: 'idle',
        errorMsg: '',
        progress: 0,
        transcribingDetail: '',
        audioDurationSec: undefined,
        noteTitle: '',
        noteText: '',
        selectedFolder: '',
        savedNoteId: null,
        requestToken: 0,

        prepareSelectedFile: file => {
          stopProgressTimer();
          bumpRequestToken();
          set({
            selectedFile: file,
            status: 'selected',
            errorMsg: '',
            progress: 0,
            transcribingDetail: '',
            audioDurationSec: undefined,
            noteTitle: file.name.replace(/\.[^.]+$/, ''),
            noteText: '',
            savedNoteId: null,
          });
        },

        setUploadError: message => {
          stopProgressTimer();
          bumpRequestToken();
          set({
            status: 'error',
            errorMsg: message,
            progress: 0,
            transcribingDetail: '',
          });
        },

        resetUpload: () => {
          stopProgressTimer();
          bumpRequestToken();
          set({
            selectedFile: null,
            status: 'idle',
            errorMsg: '',
            progress: 0,
            transcribingDetail: '',
            audioDurationSec: undefined,
            noteTitle: '',
            noteText: '',
            selectedFolder: '',
            savedNoteId: null,
          });
        },

        setNoteTitle: value => set({ noteTitle: value }),
        setNoteText: value => set({ noteText: value }),
        setSelectedFolder: value => set({ selectedFolder: value }),
        setSavedNoteId: value => set({ savedNoteId: value }),

        transcribe: async ({
          mode,
          cloudProvider,
          groqProcessingMode,
          cloudModel,
          localProvider,
          activeLocalModel,
          activeApiKey,
          language,
          normalizedCustomUrl,
          preflightError,
        }) => {
          const { selectedFile } = get();

          if (!selectedFile?.browserFile) {
            get().setUploadError(
              selectedFile
                ? 'Page refresh cleared access to the local file. Please choose it again before transcribing.'
                : 'Please choose a file from your browser before starting transcription.',
            );
            return;
          }

          if (preflightError) {
            get().setUploadError(preflightError);
            return;
          }

          const requestToken = bumpRequestToken();
          set({
            status: 'transcribing',
            errorMsg: '',
            savedNoteId: null,
            progress: 0,
            transcribingDetail: '',
            noteText: '',
            audioDurationSec: undefined,
          });
          startMockProgress(requestToken);

          try {
            const result = await transcribeFileWithStrategy(
              selectedFile.browserFile,
              {
                mode,
                cloudProvider,
                groqProcessingMode,
                cloudModel,
                localProvider,
                activeLocalModel,
                activeApiKey,
                language,
                normalizedCustomUrl,
                preflightError,
              },
              update => {
                if (get().requestToken !== requestToken) {
                  return;
                }

                if (typeof update.progress === 'number') {
                  if (update.authoritativeProgress) {
                    stopProgressTimer();
                  }
                  set({ progress: update.progress });
                }

                if (update.detail) {
                  set({ transcribingDetail: update.detail });
                }

                if (typeof update.partialText === 'string') {
                  set({ noteText: update.partialText });
                }
              },
            );

            if (get().requestToken !== requestToken) {
              return;
            }

            stopProgressTimer();
            set({
              progress: 100,
              transcribingDetail: '',
              noteText: result.text,
              audioDurationSec: result.duration as number | undefined,
              savedNoteId: result.savedNoteId || null,
              status: 'complete',
            });
          } catch (error) {
            if (get().requestToken !== requestToken) {
              return;
            }

            stopProgressTimer();
            set({
              progress: 0,
              transcribingDetail: '',
              errorMsg: error instanceof Error ? error.message : 'Transcription failed',
              status: 'error',
            });
          }
        },
      };
    },
    {
      name: 'niuniu-upload-session',
      storage: createJSONStorage(() => sessionStorage),
      partialize: state =>
        ({
          selectedFile: state.selectedFile
            ? {
                name: state.selectedFile.name,
                sizeBytes: state.selectedFile.sizeBytes,
              }
            : null,
          status: state.status,
          errorMsg: state.errorMsg,
          progress: state.progress,
          transcribingDetail: state.transcribingDetail,
          audioDurationSec: state.audioDurationSec,
          noteTitle: state.noteTitle,
          noteText: state.noteText,
          selectedFolder: state.selectedFolder,
          savedNoteId: state.savedNoteId,
        }) satisfies PersistedUploadSessionState,
      merge: (persistedState, currentState) => {
        const persisted = persistedState as PersistedUploadSessionState | undefined;
        if (!persisted) {
          return currentState;
        }

        const mergedState: UploadSessionState = {
          ...currentState,
          ...persisted,
          selectedFile: persisted.selectedFile
            ? {
                name: persisted.selectedFile.name,
                sizeBytes: persisted.selectedFile.sizeBytes,
              }
            : null,
          requestToken: 0,
        };

        if (persisted.status === 'transcribing') {
          return {
            ...mergedState,
            status: 'error',
            progress: 0,
            transcribingDetail: '',
            errorMsg:
              'Page refresh interrupted the transcription request. Please choose the file again and retry.',
          };
        }

        if (persisted.status === 'selected' && persisted.selectedFile) {
          return {
            ...mergedState,
            status: 'error',
            progress: 0,
            transcribingDetail: '',
            errorMsg:
              'Page refresh cleared the selected file. Please choose it again before transcribing.',
          };
        }

        return mergedState;
      },
    },
  ),
);
